import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export enum PageSize {
  A4 = "a4",
  LETTER = "letter",
  LEGAL = "legal",
  A3 = "a3",
  A5 = "a5",
}

export enum PageOrientation {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

export enum ImageQuality {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export interface ImageFile {
  id: string;
  file: File;
  dataUrl: string;
  name: string;
  size: number;
  type: string;
}

export interface ImageToPdfConverterState {
  // Files
  images: ImageFile[];
  
  // Conversion settings
  pageSize: PageSize;
  pageOrientation: PageOrientation;
  imageQuality: ImageQuality;
  margin: number;
  fitToPage: boolean;
  
  // Generated PDF
  generatedPdf: string | null;
  pdfFileName: string | null;
  
  // UI state
  isConverting: boolean;
  error: string | null;
  progress: number;
  hasDownloaded: boolean;
}

const initialState: ImageToPdfConverterState = {
  images: [],
  
  pageSize: PageSize.A4,
  pageOrientation: PageOrientation.PORTRAIT,
  imageQuality: ImageQuality.MEDIUM,
  margin: 20,
  fitToPage: true,
  
  generatedPdf: null,
  pdfFileName: null,
  
  isConverting: false,
  error: null,
  progress: 0,
  hasDownloaded: false,
};

// Async thunk for PDF generation
export const generatePdfAsync = createAsyncThunk(
  "imageToPdfConverter/generatePdf",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { images, pageSize, pageOrientation, imageQuality, margin, fitToPage } = state.imageToPdfConverter;
      
      if (images.length === 0) {
        throw new Error("Please add at least one image to convert");
      }
      
      // Import jsPDF dynamically
      const { jsPDF } = await import("jspdf");
      
      // Create PDF document
      const pdf = new jsPDF({
        orientation: pageOrientation,
        unit: "mm",
        format: pageSize,
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - (margin * 2);
      
      let currentY = margin;
      
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        
        // Create image element to get dimensions
        const img = new Image();
        img.src = image.dataUrl;
        
        await new Promise<void>((resolve) => {
          img.onload = () => {
            const imgWidth = img.width;
            const imgHeight = img.height;
            
            let finalWidth = imgWidth;
            let finalHeight = imgHeight;
            
            // Calculate scaling if fitToPage is enabled
            if (fitToPage) {
              const scaleX = availableWidth / imgWidth;
              const scaleY = availableHeight / imgHeight;
              const scale = Math.min(scaleX, scaleY, 1); // Don't scale up
              
              finalWidth = imgWidth * scale;
              finalHeight = imgHeight * scale;
            }
            
            // Check if we need a new page
            if (currentY + finalHeight > pageHeight - margin) {
              pdf.addPage();
              currentY = margin;
            }
            
            // Calculate position to center image horizontally
            const x = margin + (availableWidth - finalWidth) / 2;
            
            // Add image to PDF
            pdf.addImage(
              image.dataUrl,
              "JPEG",
              x,
              currentY,
              finalWidth,
              finalHeight,
              undefined,
              "FAST"
            );
            
            currentY += finalHeight + 10; // Add some spacing between images
            
            resolve();
          };
          
          img.onerror = () => {
            rejectWithValue(`Failed to load image: ${image.name}`);
            resolve();
          };
        });
      }
      
      // Generate PDF as data URL
      const pdfDataUrl = pdf.output("datauristring");
      const fileName = `converted_images_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      return { pdfDataUrl, fileName };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to generate PDF"
      );
    }
  }
);

export const ImageToPdfConverterSlice = createSlice({
  name: "imageToPdfConverter",
  initialState,
  reducers: {
    imagesAdded: (state, action: PayloadAction<ImageFile[]>) => {
      state.images.push(...action.payload);
      state.error = null;
      state.hasDownloaded = false;
    },
    
    imageRemoved: (state, action: PayloadAction<string>) => {
      state.images = state.images.filter(img => img.id !== action.payload);
      state.error = null;
    },
    
    imagesReordered: (state, action: PayloadAction<ImageFile[]>) => {
      state.images = action.payload;
    },
    
    pageSizeChanged: (state, action: PayloadAction<PageSize>) => {
      state.pageSize = action.payload;
    },
    
    pageOrientationChanged: (state, action: PayloadAction<PageOrientation>) => {
      state.pageOrientation = action.payload;
    },
    
    imageQualityChanged: (state, action: PayloadAction<ImageQuality>) => {
      state.imageQuality = action.payload;
    },
    
    marginChanged: (state, action: PayloadAction<number>) => {
      state.margin = action.payload;
    },
    
    fitToPageChanged: (state, action: PayloadAction<boolean>) => {
      state.fitToPage = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    markAsDownloaded: (state) => {
      state.hasDownloaded = true;
    },
    
    resetConverter: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generatePdfAsync.pending, (state) => {
        state.isConverting = true;
        state.error = null;
        state.progress = 0;
      })
      .addCase(generatePdfAsync.fulfilled, (state, action) => {
        state.isConverting = false;
        state.generatedPdf = action.payload.pdfDataUrl;
        state.pdfFileName = action.payload.fileName;
        state.progress = 100;
        state.error = null;
      })
      .addCase(generatePdfAsync.rejected, (state, action) => {
        state.isConverting = false;
        state.error = action.payload as string;
        state.progress = 0;
      });
  },
});

// Utility functions
export function validateImageFile(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return "Please select a valid image file";
  }
  
  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    return "Image file size must be less than 10MB";
  }
  
  return null;
}

export function generateUniqueId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export const {
  imagesAdded,
  imageRemoved,
  imagesReordered,
  pageSizeChanged,
  pageOrientationChanged,
  imageQualityChanged,
  marginChanged,
  fitToPageChanged,
  clearError,
  markAsDownloaded,
  resetConverter,
} = ImageToPdfConverterSlice.actions;

export const selectState = (state: RootState) => state.imageToPdfConverter;

export default ImageToPdfConverterSlice.reducer;
